import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Tabs from '../../ui/Tabs'
import ConfiguredAlertsTab from '../ConfiguredAlerts/ConfiguredAlertsTab'
import IssueStatusesTab from '../IssueStatuses/IssueStatusesTab'
import { updateUrlFilters } from '../../../utils/urlFilters'

const ALERTS_TAB = { label: 'Alertes', value: 'alerts' }
const STATUSES_TAB = { label: 'Statuts', value: 'statuses' }
const TAB_PARAM = 'tab'
const DEFAULT_TAB = ALERTS_TAB.value

const HEADER_CLASS = 'tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-solid '
  + 'tw:border-gray-200 tw:pb-4 tw:mb-4'
const TITLE_CLASS = 'tw:text-lg! tw:font-semibold! tw:text-gray-900! tw:m-0!'
const BACK_BUTTON_CLASS = 'tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-full! '
  + 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:text-gray-800 tw:hover:bg-gray-100'

// The Statuts tab exists only with the configurable_statuses addon; a stale
// ?tab=statuses URL (or a missing addon) falls back to the always-available Alertes tab.
const initialTab = canConfigureStatuses => {
  const urlValue = new URLSearchParams(window.location.search).get(TAB_PARAM)
  if (urlValue === STATUSES_TAB.value)
    return canConfigureStatuses ? STATUSES_TAB.value : DEFAULT_TAB
  return DEFAULT_TAB
}

const Configuration = ({ collectId, isAdmin, canConfigureStatuses, palette }) => {
  const [activeTab, setActiveTab] = useState(() => initialTab(canConfigureStatuses))

  const tabItems = useMemo(() => {
    if (canConfigureStatuses)
      return [ALERTS_TAB, STATUSES_TAB]
    return [ALERTS_TAB]
  }, [canConfigureStatuses])

  const handleTabChange = useCallback(value => {
    setActiveTab(value)
    updateUrlFilters({ [TAB_PARAM]: value === DEFAULT_TAB ? null : value })
  }, [])

  const goBack = useCallback(() => {
    window.location.assign(`/collects/${collectId}/issues`)
  }, [collectId])

  if (!isAdmin)
    return null

  // A single-tab bar is noise: hide the nav entirely when the addon leaves only Alertes.
  const showTabs = tabItems.length > 1
  const showStatuses = activeTab === STATUSES_TAB.value && canConfigureStatuses
  const tabsNav = <Tabs items={tabItems} value={activeTab} variant="neutral" onChange={handleTabChange} />

  return (
    <div className="tw:mt-4">
      <div className={HEADER_CLASS}>
        <button aria-label="Retour aux alertes" className={BACK_BUTTON_CLASS} type="button" onClick={goBack}>
          <i className="fas fa-arrow-left tw:text-xs" />
        </button>
        <h1 className={TITLE_CLASS}>Configuration</h1>
      </div>
      {showTabs ? tabsNav : null}
      <div className="tw:mt-4">
        {activeTab === ALERTS_TAB.value ? <ConfiguredAlertsTab collectId={collectId} /> : null}
        {showStatuses ? <IssueStatusesTab collectId={collectId} palette={palette} /> : null}
      </div>
    </div>
  )
}

Configuration.propTypes = {
  canConfigureStatuses: PropTypes.bool,
  collectId: PropTypes.number.isRequired,
  isAdmin: PropTypes.bool,
  palette: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
}

Configuration.defaultProps = {
  canConfigureStatuses: false,
  isAdmin: false,
  palette: {}
}

export default Configuration
